import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiEdit2,
  FiTrash2,
  FiMessageSquare,
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaSync, FaUserGraduate } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import StudentRemarksModal from "./StudentRemarksModal";
import DeleteStudentModal from "./DeleteStudentModal";
import PageHeader from "../../core/common/header/PageHeader";

interface StudentType {
  studentId: number;
  firstName: string;
  lastName: string;
  email: string;
  whatsappNumber: number;
  activeStudent: boolean;
  role: string;
  lastDegree: string; // non-null
  enrollmentDate: string | null;
}
type EnrollmentApi = any;

const ManageStudents: React.FC = () => {
  const { logout } = useAuth();
  const [students, setStudents] = useState<StudentType[]>([]);
  const [coursesByStudentId, setCoursesByStudentId] = useState<
    Record<number, string[]>
  >({});
  const [batchesByStudentId, setBatchesByStudentId] = useState<
    Record<number, string[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFor, setOpenFor] = useState<number | null>(null);
  const [deleteStudent, setDeleteStudent] = useState<StudentType | null>(null);

  const [filterCourse, setFilterCourse] = useState<string>("");
  const [filterBatch, setFilterBatch] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");

  // Tab state — "current" | "legacy"
  const [activeTab, setActiveTab] = useState<"current" | "legacy">("current");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const normalizeRole = (r: any): string => {
    if (r == null) return "";
    const t = String(r).trim().toUpperCase();
    return t.startsWith("ROLE_") ? t.slice(5) : t;
  };

  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[\s:()\-]+/g, " ")
      .trim();

  const toCourseCode = (title?: string): string => {
    const t = norm(title || "");
    switch (t) {
      case "ausbildung preparatory course":
        return "APC";
      case "german language course":
        return "GLC";
      case "student visa processing course":
        return "SVPC";
      case "ausbildung preparatory course without german language":
        return "APCW";
      case "ausbildung searching visa processing course":
        return "ASVPC";
      case "combo ausbildung searching and preparatory course":
        return "C-ASVPC";
      default:
        if (
          t.includes("ausbildung preparatory course") &&
          t.includes("without german")
        )
          return "APCW";
        if (t.includes("german language course")) return "GLC";
        if (t.includes("student visa processing course")) return "SVPC";
        if (t.includes("ausbildung searching visa processing course"))
          return "ASVPC";
        if (
          t.includes("combo") &&
          t.includes("ausbildung") &&
          t.includes("preparatory") &&
          t.includes("searching")
        )
          return "C-ASVPC";
        return "OTHER";
    }
  };

  const getStudentIdFromEnrollment = (e: EnrollmentApi): number | null => {
    if (typeof e.studentId === "number") return e.studentId;
    if (e.student && typeof e.student.studentId === "number")
      return e.student.studentId;
    return null;
  };

  const getStudentEmailFromEnrollment = (e: EnrollmentApi): string | null => {
    if (typeof e.studentEmail === "string") return e.studentEmail;
    if (e.student && typeof e.student.email === "string")
      return e.student.email;
    return null;
  };

  const isBatchActive = (e: EnrollmentApi): boolean => {
    const v =
      e.batchIsActive ??
      e.isActiveBatch ??
      e.batchActive ??
      (e.batch && (e.batch.isActive ?? e.batch.active ?? e.batch.is_active));
    return typeof v === "boolean" ? v : true;
  };

  const getEnrollmentDate = (e: EnrollmentApi): string | null => {
    const date =
      e.enrollmentDate || e.enrollment_date || e.createdAt || e.created_at;
    return date ? String(date) : null;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resStudents, resEnrollments] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/students`, {
          credentials: "include",
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/enrollments`, {
          credentials: "include",
        }),
      ]);

      if (!resStudents.ok) {
        if (resStudents.status === 401) logout();
        throw new Error(`Error ${resStudents.status}: Failed to load students`);
      }
      if (!resEnrollments.ok) {
        if (resEnrollments.status === 401) logout();
        throw new Error(
          `Error ${resEnrollments.status}: Failed to load enrollments`
        );
      }

      const rawStudents = await resStudents.json();
      const rawEnrollments = await resEnrollments.json();

      const normalizedStudents: StudentType[] = (
        Array.isArray(rawStudents) ? rawStudents : []
      ).map((s: any) => ({
        studentId: s.studentId ?? s.id,
        firstName: s.firstName,
        lastName: s.lastName,
        email: s.email,
        whatsappNumber: s.whatsappNumber,
        activeStudent:
          s.activeStudent ?? s.isActiveStudent ?? s.active ?? false,
        role: normalizeRole(s.role),
        lastDegree: String(s.lastDegree ?? s.last_degree ?? "").trim(),
        enrollmentDate:
          s.enrollmentDate ||
          s.enrollment_date ||
          s.createdAt ||
          s.created_at ||
          null,
      }));
      const onlyStudents = normalizedStudents.filter(
        (u) => u.role === "STUDENT"
      );

      const idByEmail: Record<string, number> = {};
      for (const s of onlyStudents)
        if (s.email) idByEmail[s.email.toLowerCase()] = s.studentId;

      const enrollments: EnrollmentApi[] = Array.isArray(rawEnrollments)
        ? rawEnrollments
        : rawEnrollments.enrollments || [];
      const coursesMap: Record<number, Set<string>> = {};
      const batchesMap: Record<number, Set<string>> = {};
      const enrollmentDatesMap: Record<number, string> = {};

      for (const e of enrollments) {
        let sid = getStudentIdFromEnrollment(e);
        if (sid == null) {
          const email = getStudentEmailFromEnrollment(e);
          if (email && idByEmail[email.toLowerCase()])
            sid = idByEmail[email.toLowerCase()];
        }
        if (sid == null) continue;

        const courseTitle = e.courseTitle || e.course?.title || "";
        (coursesMap[sid] ||= new Set()).add(toCourseCode(courseTitle));

        const batchName: string | undefined =
          e.batchName || e.batch?.name || undefined;
        if (batchName && isBatchActive(e))
          (batchesMap[sid] ||= new Set()).add(batchName);

        // Track earliest enrollment date
        const enrollDate = getEnrollmentDate(e);
        if (enrollDate) {
          if (
            !enrollmentDatesMap[sid] ||
            enrollDate < enrollmentDatesMap[sid]
          ) {
            enrollmentDatesMap[sid] = enrollDate;
          }
        }
      }

      // Update students with enrollment dates from enrollments if not already set
      onlyStudents.forEach((student) => {
        if (!student.enrollmentDate && enrollmentDatesMap[student.studentId]) {
          student.enrollmentDate = enrollmentDatesMap[student.studentId];
        }
      });

      const nextCourses: Record<number, string[]> = {};
      const nextBatches: Record<number, string[]> = {};
      Object.keys(coursesMap).forEach(
        (k) => (nextCourses[+k] = Array.from(coursesMap[+k]))
      );
      Object.keys(batchesMap).forEach(
        (k) => (nextBatches[+k] = Array.from(batchesMap[+k]))
      );

      setStudents(onlyStudents);
      setCoursesByStudentId(nextCourses);
      setBatchesByStudentId(nextBatches);
      setError(null);
    } catch (e: any) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterCourse,
    filterBatch,
    search,
    activeTab,
    filterDateFrom,
    filterDateTo,
  ]);

  const courseOptions = useMemo(() => {
    const s = new Set<string>();
    Object.values(coursesByStudentId).forEach((arr) =>
      arr.forEach((c) => s.add(c))
    );
    return Array.from(s).sort();
  }, [coursesByStudentId]);

  const batchOptions = useMemo(() => {
    const s = new Set<string>();
    Object.values(batchesByStudentId).forEach((arr) =>
      arr.forEach((b) => s.add(b))
    );
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [batchesByStudentId]);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();

    const list = students.filter((stu) => {
      const codes = coursesByStudentId[stu.studentId] || [];
      const batches = batchesByStudentId[stu.studentId] || [];

      if (filterCourse && !codes.includes(filterCourse)) return false;
      if (filterBatch && !batches.includes(filterBatch)) return false;

      // Date filtering
      if (filterDateFrom || filterDateTo) {
        if (!stu.enrollmentDate) return false;

        const enrollDate = new Date(stu.enrollmentDate);

        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          if (enrollDate < fromDate) return false;
        }

        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire end date
          if (enrollDate > toDate) return false;
        }
      }

      if (!q) return true;

      const hay = [
        stu.firstName,
        stu.lastName,
        stu.email,
        String(stu.whatsappNumber ?? ""),
        String(stu.studentId),
        ...codes,
        ...batches,
        stu.lastDegree,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });

    list.sort((a, b) => (b.studentId ?? 0) - (a.studentId ?? 0));
    return list;
  }, [
    students,
    coursesByStudentId,
    batchesByStudentId,
    filterCourse,
    filterBatch,
    search,
    filterDateFrom,
    filterDateTo,
  ]);

  // Split for tabs
  const legacyStudents = useMemo(
    () =>
      filteredStudents.filter((s) => s.lastDegree.toLowerCase() === "legacy"),
    [filteredStudents]
  );
  const currentStudents = useMemo(
    () =>
      filteredStudents.filter((s) => s.lastDegree.toLowerCase() !== "legacy"),
    [filteredStudents]
  );

  // Get current tab data
  const displayedStudents =
    activeTab === "current" ? currentStudents : legacyStudents;

  // Pagination calculations
  const totalPages = Math.ceil(displayedStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = displayedStudents.slice(startIndex, endIndex);

  const clearFilters = () => {
    setFilterCourse("");
    setFilterBatch("");
    setSearch("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const Chips = ({
    items,
    color = "gray",
  }: {
    items: string[];
    color?: "gray" | "blue";
  }) => {
    if (!items || items.length === 0)
      return <span className="text-gray-400 text-xs">—</span>;
    const base =
      color === "blue"
        ? "bg-blue-50 text-blue-800 border-blue-200"
        : "bg-gray-100 text-gray-800 border-gray-200";
    return (
      <div className="flex flex-nowrap gap-1 overflow-hidden">
        {items.map((txt, i) => (
          <span
            key={txt + i}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${base} whitespace-nowrap`}
            title={txt}
          >
            {txt}
          </span>
        ))}
      </div>
    );
  };

  const handleDeleteClick = (student: StudentType) => setDeleteStudent(student);
  const handleDeleteConfirm = async () => {
    if (!deleteStudent) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/${deleteStudent.studentId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setDeleteStudent(null);
      fetchData();
    } catch (e: any) {
      alert("Delete error: " + e.message);
    }
  };

  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
      {/* Items per page */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Show:</span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-gray-600">per page</span>
      </div>

      {/* Page info */}
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1} to{" "}
        {Math.min(endIndex, displayedStudents.length)} of{" "}
        {displayedStudents.length} students
      </div>

      {/* Page buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronLeft />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                  currentPage === pageNum
                    ? "bg-[#009fe3] text-white border-[#009fe3]"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronRight />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Last
        </button>
      </div>
    </div>
  );

  const TableSection: React.FC<{ rows: StudentType[] }> = ({ rows }) => (
    <>
      <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
        {/* Mobile: Horizontal scroll wrapper */}
        <div className="overflow-x-auto">
          <table className="min-w-[1300px] w-full">
            <thead className="bg-[#009fe3] text-white">
              <tr className="text-left text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">WhatsApp</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3 font-semibold">Enrollment Date</th>
                <th className="px-4 py-3 font-semibold">Courses</th>
                <th className="px-4 py-3 font-semibold">Batches</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No students match your filters.
                  </td>
                </tr>
              ) : (
                rows.map((stu, idx) => {
                  const courseCodes = coursesByStudentId[stu.studentId] || [];
                  const batchNames = batchesByStudentId[stu.studentId] || [];
                  const canDelete = batchNames.length === 0;
                  return (
                    <tr
                      key={stu.studentId}
                      className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"}
                    >
                      <td className="px-4 py-3 text-gray-700">
                        {stu.studentId}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {stu.firstName} {stu.lastName}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{stu.email}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {stu.whatsappNumber || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                            stu.activeStudent
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {stu.activeStudent ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex items-center gap-1">
                          {formatDate(stu.enrollmentDate)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Chips items={courseCodes} />
                      </td>
                      <td className="px-4 py-3">
                        <Chips items={batchNames} color="blue" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-nowrap gap-2 justify-end whitespace-nowrap">
                          <button
                            onClick={() => setOpenFor(stu.studentId)}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-yellow-400 text-white hover:bg-yellow-500 transition-colors"
                          >
                            <FiMessageSquare /> Remarks
                          </button>
                          <Link
                            to={`/admin/edit-student/${stu.studentId}?from=students`}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-[#009fe3] text-white hover:bg-[#007cb5] transition-colors"
                          >
                            <FiEdit2 /> Edit
                          </Link>
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteClick(stu)}
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
                              title="No active batch — safe to delete"
                            >
                              <FiTrash2 /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile scroll hint */}
        <div className="md:hidden bg-gray-50 border-t border-gray-200 px-4 py-2 text-center">
          <p className="text-xs text-gray-500">
            ← Scroll horizontally to see all columns →
          </p>
        </div>
      </div>

      {/* Pagination Controls */}
      {rows.length > 0 && <PaginationControls />}
    </>
  );

  const currentCount = currentStudents.length;
  const legacyCount = legacyStudents.length;

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaUserGraduate />}
        title="Manage Students"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Students" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white rounded-xl shadow-sm border p-3 sm:p-4 mb-4">
          <div className="grid gap-3 md:grid-cols-12 items-end">
            <div className="md:col-span-12">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Search
              </label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, WhatsApp, course code, batch, legacy…"
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] text-sm"
                  style={{ fontSize: "16px" }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear"
                  >
                    <FiX />
                  </button>
                )}
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Course
              </label>
              <select
                value={filterCourse}
                onChange={(e) => setFilterCourse(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3] text-sm"
                style={{ fontSize: "16px" }}
              >
                <option value="">All Courses</option>
                {courseOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Batch
              </label>
              <select
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3] text-sm"
                style={{ fontSize: "16px" }}
              >
                <option value="">All Batches</option>
                {batchOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Enrollment From
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3] text-sm"
                style={{ fontSize: "16px" }}
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Enrollment To
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3] text-sm"
                style={{ fontSize: "16px" }}
              />
            </div>
          </div>

          {(filterCourse ||
            filterBatch ||
            search ||
            filterDateFrom ||
            filterDateTo) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {filterCourse && (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                  Course: {filterCourse}
                  <button
                    onClick={() => setFilterCourse("")}
                    className="hover:text-gray-900"
                    title="Remove"
                  >
                    <FiX />
                  </button>
                </span>
              )}
              {filterBatch && (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                  Batch: {filterBatch}
                  <button
                    onClick={() => setFilterBatch("")}
                    className="hover:text-gray-900"
                    title="Remove"
                  >
                    <FiX />
                  </button>
                </span>
              )}
              {search && (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                  Search: "{search}"
                  <button
                    onClick={() => setSearch("")}
                    className="hover:text-gray-900"
                    title="Remove"
                  >
                    <FiX />
                  </button>
                </span>
              )}
              {filterDateFrom && (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                  From: {new Date(filterDateFrom).toLocaleDateString("en-GB")}
                  <button
                    onClick={() => setFilterDateFrom("")}
                    className="hover:text-gray-900"
                    title="Remove"
                  >
                    <FiX />
                  </button>
                </span>
              )}
              {filterDateTo && (
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full">
                  To: {new Date(filterDateTo).toLocaleDateString("en-GB")}
                  <button
                    onClick={() => setFilterDateTo("")}
                    className="hover:text-gray-900"
                    title="Remove"
                  >
                    <FiX />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-4">
          <div className="inline-flex rounded-lg border bg-white shadow-sm overflow-hidden">
            <button
              onClick={() => setActiveTab("current")}
              className={`px-4 py-2 text-sm font-semibold ${
                activeTab === "current"
                  ? "bg-[#009fe3] text-white"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              Current Students{" "}
              <span className="opacity-80">({currentCount})</span>
            </button>
            <button
              onClick={() => setActiveTab("legacy")}
              className={`px-4 py-2 text-sm font-semibold border-l ${
                activeTab === "legacy"
                  ? "bg-[#009fe3] text-white"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              Legacy Students{" "}
              <span className="opacity-80">({legacyCount})</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading students...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={fetchData}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <TableSection rows={paginatedStudents} />
        )}
      </main>

      {openFor !== null && (
        <StudentRemarksModal
          studentId={openFor}
          onClose={() => setOpenFor(null)}
          refreshList={fetchData}
          role="admin"
        />
      )}

      {deleteStudent && (
        <DeleteStudentModal
          studentName={`${deleteStudent.firstName} ${deleteStudent.lastName}`}
          studentEmail={deleteStudent.email}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteStudent(null)}
        />
      )}
    </div>
  );
};

export default ManageStudents;
